import React, { useState } from 'react';

function AddExpense({ categories = [], loadingCategories = false, accounts = [], onAdd, onCancel }) {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [bucketId, setBucketId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Split mode state
  const [isSplit, setIsSplit] = useState(false);
  const [splits, setSplits] = useState([
    { categoryId: '', amount: '' },
    { categoryId: '', amount: '' }
  ]);

  // Calculate split sum in real-time
  const calculateSplitSum = () => {
    return splits.reduce((sum, split) => {
      const splitAmount = parseFloat(split.amount) || 0;
      return sum + splitAmount;
    }, 0);
  };

  const splitSum = calculateSplitSum();
  const totalAmount = parseFloat(amount) || 0;
  const splitSumMatches = Math.abs(splitSum - totalAmount) < 0.01;
  
  // Check if all splits are filled
  const allSplitsFilled = splits.every(s => s.categoryId && s.amount);
  
  // Validation: splits must sum to total when in split mode
  const isSplitValid = !isSplit || (allSplitsFilled && splitSumMatches);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const expenseData = {
        description: description.trim(),
        amount: parseFloat(amount),
        date,
        entryMethod: 'manual',
        importTemplate: null,
        bucketId: bucketId || null,
        note: note.trim() || null
      };

      // Add splits or category based on mode
      if (isSplit) {
        // Send splits array with parsed amounts
        expenseData.splits = splits.map(split => ({
          categoryId: split.categoryId,
          amount: parseFloat(split.amount)
        }));
      } else {
        // Send category field for simple expenses
        expenseData.category = category;
      }

      await onAdd(expenseData);
    } catch (err) {
      // Handle backend validation errors
      console.error('Error adding expense:', err);
      
      // Extract error message from different error formats
      let errorMessage = 'Failed to add expense. Please try again.';
      
      if (err.response?.data?.body) {
        // API Gateway error format
        errorMessage = err.response.data.body;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }}>
      <div className="card" style={{
        width: '450px',
        margin: 0,
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3 style={{
          color: '#ff6b35',
          marginBottom: '24px',
          fontSize: '1.5rem',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          Add New Expense
        </h3>

        {/* Error message display */}
        {error && (
          <div style={{
            marginBottom: '20px',
            padding: '12px 16px',
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: '6px',
            color: '#c33',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What did you spend on?"
              required
            />
          </div>

          <div className="form-group">
            <label>Amount ($)</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          {/* Split mode toggle */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={isSplit}
                onChange={(e) => setIsSplit(e.target.checked)}
                style={{ width: 'auto', margin: 0 }}
              />
              Split across categories
            </label>
            <div style={{
              fontSize: '12px',
              color: '#888',
              marginTop: '4px'
            }}>
              Divide this expense across multiple categories
            </div>
          </div>

          {/* Simple category selection (when not split) */}
          {!isSplit && (
            <div className="form-group">
              <label>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                disabled={loadingCategories}
              >
                <option value="">
                  {loadingCategories ? 'Loading categories...' : 'Select a category'}
                </option>
                {categories.map(cat => {
                  // Check if this is a subcategory by looking for parentId or parent info
                  const isSubcategory = cat.parentId || cat.parent;
                  const displayName = isSubcategory 
                    ? `  ↳ ${cat.name}` // Indent subcategories with arrow
                    : cat.name;
                  
                  return (
                    <option key={cat.id || cat.name} value={cat.name}>
                      {cat.icon ? `${cat.icon} ${displayName}` : displayName}
                    </option>
                  );
                })}
              </select>
              <div style={{
                fontSize: '12px',
                color: '#888',
                marginTop: '4px'
              }}>
                Choose from any category or subcategory
              </div>
            </div>
          )}

          {/* Split entry interface (when split mode is enabled) */}
          {isSplit && (
            <div className="form-group">
              <label>Category Splits</label>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #404040'
              }}>
                {splits.map((split, index) => (
                  <div key={index} style={{
                    marginBottom: index < splits.length - 1 ? '12px' : '0',
                    paddingBottom: index < splits.length - 1 ? '12px' : '0',
                    borderBottom: index < splits.length - 1 ? '1px solid #404040' : 'none'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#b0b0b0'
                      }}>
                        Split {index + 1}
                      </div>
                      {splits.length > 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newSplits = splits.filter((_, i) => i !== index);
                            setSplits(newSplits);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc3545',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#ffe0e0'}
                          onMouseLeave={(e) => e.target.style.background = 'none'}
                          title="Remove this split"
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <select
                        value={split.categoryId}
                        onChange={(e) => {
                          const newSplits = [...splits];
                          newSplits[index].categoryId = e.target.value;
                          setSplits(newSplits);
                        }}
                        required
                        disabled={loadingCategories}
                        style={{ flex: 2 }}
                      >
                        <option value="">
                          {loadingCategories ? 'Loading...' : 'Select category'}
                        </option>
                        {categories.map(cat => {
                          const isSubcategory = cat.parentId || cat.parent;
                          const displayName = isSubcategory 
                            ? `  ↳ ${cat.name}`
                            : cat.name;
                          
                          return (
                            <option key={cat.id || cat.name} value={cat.name}>
                              {cat.icon ? `${cat.icon} ${displayName}` : displayName}
                            </option>
                          );
                        })}
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        value={split.amount}
                        onChange={(e) => {
                          const newSplits = [...splits];
                          newSplits[index].amount = e.target.value;
                          setSplits(newSplits);
                        }}
                        placeholder="0.00"
                        required
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                ))}
                
                {/* Add Another Split button */}
                <button
                  type="button"
                  onClick={() => {
                    setSplits([...splits, { categoryId: '', amount: '' }]);
                  }}
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '10px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '2px dashed #404040',
                    borderRadius: '6px',
                    color: '#888',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#ff6b35';
                    e.target.style.color = '#ff6b35';
                    e.target.style.background = 'rgba(255, 107, 53, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#404040';
                    e.target.style.color = '#888';
                    e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                  }}
                >
                  + Add Another Split
                </button>

                {/* Real-time validation display */}
                {isSplit && amount && (
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    borderRadius: '6px',
                    background: splitSumMatches ? 'rgba(46, 213, 115, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                    border: `1px solid ${splitSumMatches ? 'rgba(46, 213, 115, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '4px'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#b0b0b0'
                      }}>
                        Total Expense:
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#f0f0f0'
                      }}>
                        ${totalAmount.toFixed(2)}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#b0b0b0'
                      }}>
                        Split Sum:
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: splitSumMatches ? '#2ed573' : '#ffc107',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        ${splitSum.toFixed(2)}
                        {splitSumMatches ? (
                          <span style={{ fontSize: '16px' }}>✓</span>
                        ) : (
                          <span style={{ fontSize: '16px' }}>⚠️</span>
                        )}
                      </div>
                    </div>
                    {!splitSumMatches && allSplitsFilled && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#ffc107',
                        fontWeight: '500'
                      }}>
                        Split amounts must equal the total expense amount
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#888',
                marginTop: '8px'
              }}>
                Assign amounts to each category (must total ${amount || '0.00'})
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any additional details..."
              rows={3}
            />
            <div style={{
              fontSize: '12px',
              color: '#888',
              marginTop: '4px'
            }}>
              Any extra context about this expense
            </div>
          </div>

          {/* Bucket assignment dropdown */}
          {accounts.length > 0 && (
            <div className="form-group">
              <label>Bucket (optional)</label>
              <select
                value={bucketId}
                onChange={(e) => setBucketId(e.target.value)}
              >
                <option value="">No bucket</option>
                {accounts.map(account => (
                  account.buckets && account.buckets.length > 0 && (
                    <optgroup key={account.id} label={account.name}>
                      {account.buckets.map(bucket => (
                        <option key={bucket.id} value={bucket.id}>
                          {bucket.name}
                        </option>
                      ))}
                    </optgroup>
                  )
                ))}
              </select>
              <div style={{
                fontSize: '12px',
                color: '#888',
                marginTop: '4px'
              }}>
                Assign this expense to a savings bucket
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button 
              type="submit" 
              className="btn" 
              style={{ flex: 1 }}
              disabled={!isSplitValid || isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Expense'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="btn"
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #666, #555)',
                boxShadow: '0 2px 8px rgba(102, 102, 102, 0.3)'
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddExpense;